import React, { useState, useRef, useEffect } from 'react';
import { confirmOtp } from '../../../api/users';
import { identifyFullStoryUser } from '../../../utils/fullstory';
import { handleUserRedirect } from '../../../utils/userRedirect';
import { useHistory } from 'react-router-dom';

import styles from './index.module.css';

const CODE_LENGTH = 6;
const EMPTY_DIGITS = Array(CODE_LENGTH).fill('');

const OtpEntry = ({
  onSubmit,
  onSuccess,
  onCancel,
  onRecoveryClick,
  standalone = false,
  title = 'Enter Verification Code',
  subtitle = 'Enter the 6-digit code from your authenticator app',
  isLoading: externalIsLoading = false,
  error: externalError = null,
}) => {
  const history = useHistory();
  const [digits, setDigits] = useState(EMPTY_DIGITS);
  const digitRefs = useRef([]);
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const isLoading = standalone ? internalIsLoading : externalIsLoading;
  const error = standalone ? internalError : externalError;

  const handleStandaloneOtpSubmit = async (otpCode) => {
    if (!standalone) return;

    setInternalIsLoading(true);
    setInternalError(null);

    try {
      const response = await confirmOtp(otpCode);

      if (response.success) {
        if (onSuccess) {
          onSuccess(response);
        } else {
          identifyFullStoryUser(response.user);
          handleUserRedirect(response.user, history, {
            isImpersonation: false,
          });
        }
      } else {
        setInternalError(response.error || 'Invalid verification code. Please try again.');
        resetDigits();
      }
    } catch (e) {
      setInternalError('Network error. Please check your connection and try again.');
      resetDigits();
    } finally {
      setInternalIsLoading(false);
    }
  };

  const submitCode = (code) => {
    if (standalone) {
      handleStandaloneOtpSubmit(code);
    } else if (onSubmit) {
      onSubmit(code);
    }
  };

  const resetDigits = () => {
    setDigits(EMPTY_DIGITS);
    if (digitRefs.current[0]) digitRefs.current[0].focus();
  };

  // Single-digit sanitizer -- OTP codes are numeric only.
  const sanitizeChar = (char) => char.replace(/\D/g, '').slice(0, 1);

  const clearError = () => {
    if (standalone && internalError) setInternalError(null);
  };

  const handleDigitChange = (index, rawValue) => {
    clearError();

    const sanitized = sanitizeChar(rawValue);
    if (!sanitized) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }

    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);

    if (index < CODE_LENGTH - 1) {
      const nextRef = digitRefs.current[index + 1];
      if (nextRef) nextRef.focus();
    }

    const joined = next.join('');
    if (joined.length === CODE_LENGTH && next.every((d) => d !== '')) {
      submitCode(joined);
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      const prevRef = digitRefs.current[index - 1];
      if (prevRef) prevRef.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      digitRefs.current[index - 1].focus();
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      digitRefs.current[index + 1].focus();
    }
  };

  const handleDigitPaste = (event) => {
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    event.preventDefault();
    clearError();

    const next = [...EMPTY_DIGITS];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setDigits(next);

    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    const targetRef = digitRefs.current[focusIndex];
    if (targetRef) targetRef.focus();

    if (pasted.length === CODE_LENGTH) submitCode(pasted);
  };

  useEffect(() => {
    if (digitRefs.current[0]) digitRefs.current[0].focus();
  }, []);

  // When the parent reports an error in embedded mode, clear the digits so
  // the user can retype without manually deleting six boxes.
  useEffect(() => {
    if (!standalone && externalError) {
      setDigits(EMPTY_DIGITS);
      if (digitRefs.current[0]) digitRefs.current[0].focus();
    }
  }, [standalone, externalError]);

  const renderDigits = () => (
    <div className={styles.code_digits} onPaste={handleDigitPaste}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(node) => {
            digitRefs.current[index] = node;
          }}
          type='text'
          inputMode='numeric'
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          className={`${styles.code_digit} ${error ? styles.code_digit_error : ''}`}
          value={digit}
          onChange={(event) => handleDigitChange(index, event.target.value)}
          onKeyDown={(event) => handleDigitKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
          maxLength={1}
          disabled={isLoading}
        />
      ))}
    </div>
  );

  if (!standalone) {
    return (
      <div className={styles.embedded_content}>
        {error && <p className={styles.auth_error}>{error}</p>}
        {renderDigits()}
        <p className={styles.code_help}>
          {isLoading ? 'Verifying…' : 'Enter or paste your 6-digit authentication code'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.auth_card_header}>
            <h1 className={styles.auth_heading}>{title}</h1>
            {subtitle && <p className={styles.auth_subtitle}>{subtitle}</p>}
          </div>
          {error && <p className={styles.auth_error}>{error}</p>}
          {renderDigits()}
          <p className={styles.code_help}>
            {isLoading ? 'Verifying…' : 'Enter or paste your 6-digit authentication code'}
          </p>
          {onRecoveryClick && (
            <button
              type='button'
              className={`${styles.auth_link} ${styles.auth_link_center}`}
              onClick={onRecoveryClick}
              disabled={isLoading}
            >
              Use recovery code instead
            </button>
          )}
          {onCancel && (
            <button type='button' className={styles.auth_button_outline} onClick={onCancel} disabled={isLoading}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpEntry;
